import { useEffect, useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import { auth, db } from "../firebase";
import { collection, addDoc, updateDoc, doc, serverTimestamp, deleteField } from "firebase/firestore";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DatePicker } from "./ui/date-picker";

import {
  DEFAULT_STEP_MINUTES,
  STEP_MINUTE_OPTIONS,
  type CareerGoal,
  type GoalCategory,
  type GoalStep as LessonType,
} from "../utils/goals";
import { formatMinutes } from "../utils/prep";
import { cn } from "../lib/utils";

type AddCareerGoalProps = {
  isOpen: boolean;
  onClose: () => void;
  editGoal?: CareerGoal;
};

export default function AddCareerGoal({ isOpen, onClose, editGoal }: AddCareerGoalProps) {
  // STATE: Form fields
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<GoalCategory>("skill");
  const [targetDate, setTargetDate] = useState("");
  const [lessons, setLessons] = useState<LessonType[]>([]);
  const [stepMinutes, setStepMinutes] = useState(DEFAULT_STEP_MINUTES);
  const [newLessonTitle, setNewLessonTitle] = useState("");
  const [saving, setSaving] = useState(false);

  // EFFECT: Populate form when editing an existing goal
  useEffect(() => {
    if (editGoal) {
      setTitle(editGoal.title);
      setCategory(editGoal.category);
      setTargetDate(editGoal.targetDate || "");
      setLessons(editGoal.lessons);
      setStepMinutes(editGoal.stepMinutes ?? DEFAULT_STEP_MINUTES);
    } else {
      // Reset form when adding new goal
      resetForm();
    }
  }, [editGoal, isOpen]);

  // FUNCTION: Reset all form fields
  const resetForm = () => {
    setTitle("");
    setCategory("skill");
    setTargetDate("");
    setLessons([]);
    setStepMinutes(DEFAULT_STEP_MINUTES);
    setNewLessonTitle("");
  };

  // FUNCTION: Add a new lesson to the lessons array
  const handleAddLesson = () => {
    if (!newLessonTitle.trim()) return;

    const newLesson: LessonType = {
      id: Date.now().toString(), // Simple ID generation (timestamp)
      title: newLessonTitle.trim(),
      completed: false,
    };

    setLessons([...lessons, newLesson]);
    setNewLessonTitle(""); // Clear input after adding
  };

  // FUNCTION: Remove a lesson from the array
  const handleRemoveLesson = (lessonId: string) => {
    setLessons(lessons.filter(lesson => lesson.id !== lessonId));
  };

  // FUNCTION: Handle form submission (save to Firestore)
  const handleSubmit = async () => {
    // VALIDATION: Ensure required fields are filled
    if (!title.trim()) {
      alert("Please enter a goal");
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      alert("Please log in to save goals");
      return;
    }

    setSaving(true);

    try {
      // PREPARE DATA: Create clean data object for Firestore
      const goalData: any = {
        title: title.trim(),
        category: category,
        lessons: lessons,
        stepMinutes,
      };

      // Only add targetDate if it has a value (avoid undefined in Firestore)
      if (targetDate) {
        goalData.targetDate = targetDate;
      } else if (editGoal?.targetDate) {
        goalData.targetDate = deleteField();
      }

      if (editGoal) {
        // UPDATE EXISTING GOAL
        // updateDoc only updates the fields we specify
        await updateDoc(doc(db, "users", user.uid, "careerGoals", editGoal.id), goalData);
        console.log("✅ Goal updated successfully");
      } else {
        // ADD NEW GOAL
        // addDoc creates a new document with auto-generated ID
        await addDoc(collection(db, "users", user.uid, "careerGoals"), {
          ...goalData,
          createdAt: serverTimestamp(), // Server timestamp for consistency
        });
        console.log("✅ Goal added successfully");
      }

      // Reset form and close modal
      resetForm();
      onClose();
    } catch (error) {
      console.error("Error saving goal:", error);
      alert("Failed to save goal. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Don't render anything if modal is not open
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent showClose={false} className="flex flex-col gap-0 max-w-2xl p-0 overflow-hidden shadow-2xl">
        {/* MODAL HEADER */}
        <div className="border-b border-cocoa/20 px-6 py-4 flex items-center justify-between">
          <div>
            <DialogTitle className="text-xl">
              {editGoal ? 'Edit goal' : 'Add a goal'}
            </DialogTitle>
            <DialogDescription className="mt-1">
              Break it into steps. Each step gets a day in your prep plan.
            </DialogDescription>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-cocoa/5 transition"
          >
            <X size={20} className="text-espresso"/>
          </button>
        </div>

        {/* MODAL BODY - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* GOAL TITLE INPUT */}
          <div>
            <label className="block text-sm font-medium text-espresso mb-2">
              Goal *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Finish the AWS Cloud Practitioner course"
              className="w-full px-4 py-3 rounded-lg border border-cocoa/40 focus:border-caramel text-espresso focus:ring-2 focus:ring-caramel/20  outline-none transition placeholder:text-cocoa/40"
              autoFocus
            />
          </div>

          {/* CATEGORY SELECT */}
          <div>
            <label className="block text-sm font-medium text-espresso mb-2">
              Category *
            </label>
            <Select value={category} onValueChange={(v) => setCategory(v as GoalCategory)}>
              <SelectTrigger className="h-auto px-4 py-3 border-cocoa/40 text-cocoa bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="skill">Skill</SelectItem>
                <SelectItem value="certification">Certification</SelectItem>
                <SelectItem value="experience">Experience</SelectItem>
                <SelectItem value="networking">Networking</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* TARGET DATE INPUT */}
          <div>
            <label className="block text-sm font-medium text-espresso mb-2">
              Target Date
            </label>
            <DatePicker
              value={targetDate}
              onChange={setTargetDate}
              className="w-full px-4 py-3 rounded-lg bg-white border border-cocoa/40 focus:border-caramel text-cocoa focus:ring-2 focus:ring-caramel/20 outline-none transition"
            />
          </div>

          {/* STEP LENGTH: how much of a day the planner gives each step */}
          <div>
            <label className="block text-sm font-medium text-espresso mb-2">
              Each step takes about
            </label>
            <div className="flex flex-wrap gap-2">
              {STEP_MINUTE_OPTIONS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setStepMinutes(m)}
                  className={cn(
                    "rounded-lg border px-3 py-1.5 text-sm transition",
                    stepMinutes === m ? "border-caramel bg-caramel/10 text-espresso" : "border-cocoa/40 text-cocoa hover:border-caramel"
                  )}
                >
                  {formatMinutes(m)}
                </button>
              ))}
            </div>
          </div>

          {/* LESSONS/STEPS SECTION */}
          <div>
            <label className="block text-sm font-medium text-espresso mb-2">
              Steps
            </label>
            
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newLessonTitle}
                onChange={(e) => setNewLessonTitle(e.target.value)}
                onKeyPress={(e) => {
                  // Allow Enter key to add lesson
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddLesson();
                  }
                }}
                placeholder="e.g., Module 1: Cloud concepts"
                className="flex-1 px-4 py-2.5 rounded-lg border border-cocoa/40 text-espresso focus:border-caramel focus:ring-2 focus:ring-caramel/20 outline-none transition text-sm placeholder:text-cocoa/40"
              />
              <button
                onClick={handleAddLesson}
                className="px-4 py-2.5 rounded-lg bg-caramel text-white hover:opacity-90 transition flex items-center gap-2"
              >
                <Plus size={16} />
                Add
              </button>
            </div>

            {/* LESSONS LIST */}
          
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {lessons.map((lesson, index) => (
                  <div
                    key={lesson.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-cocoa/40 bg-white hover:border-caramel transition group"
                  >
                    <span className="text-sm font-medium text-cocoa w-6">
                      {index + 1}.
                    </span>
                    <span className="flex-1 text-sm text-espresso">
                      {lesson.title}
                    </span>
                    <button
                      onClick={() => handleRemoveLesson(lesson.id)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md text-cocoa/70 hover:text-rose-600 hover:bg-rose-50 transition"
                    > 
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            <p className="text-xs text-cocoa mt-2">
              {lessons.length} step{lessons.length !== 1 ? 's' : ''} added
            </p>
          </div>
        </div>

        {/* MODAL FOOTER */}
        <div className="border-t border-cocoa/20 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-6 py-2.5 rounded-lg border border-cocoa/50 text-espresso hover:bg-cocoa/5 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !title.trim()}
            className="px-6 py-2.5 rounded-lg bg-caramel text-white hover:opacity-90 transition disabled:opacity-50"
          >
            {saving ? 'Saving...' : (editGoal ? 'Save changes' : 'Add goal')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}